import { useTranslate } from 'hooks/useTranslate';
import { FC, ReactElement, useMemo } from 'react';

import { formatNumber } from 'utils/formats';

import Page from 'modules/_shared/components/Pages/Page';
import GoBackButton from 'modules/_shared/components/Buttons/GoBackButton';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';

import EncumbranceDetailsPageBody from './components/EncumbranceDetailsPageBody';
import { useEncumbranceDetailsPage } from './context/EncumbranceDetailsPageContext';

import './EncumbranceDetails.scss';

import {
  EncumbranceOption,
  encumbranceOptions,
} from '../../constants/encumbranceOptions';

const EncumbranceDetails: FC = () => {
  const { t } = useTranslate();

  const { data, actions } = useEncumbranceDetailsPage();

  const pageActions: ReactElement[] = [
    <GoBackButton key="go-back" />,
    <Button
      onClick={actions.handleEditEncumbrance}
      text={t('Edit')}
      disabled={data.isReadOnly}
    />,
    <Button
      variant={ButtonVariants.DANGER}
      onClick={actions.handleRemoveEncumbrance}
      text={t('Remove')}
      disabled
    />,
  ];

  const encumbranceLabel = useMemo(() => {
    const selectedEncumbrance = encumbranceOptions.find(
      (option) => option.value === data?.type
    ) as EncumbranceOption | undefined;

    return selectedEncumbrance ? selectedEncumbrance.label : '';
  }, [data?.type]);

  return (
    <Page>
      <PageListHeader
        subTitle={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <>
            {t('EncumbranceDetails')}{' '}
            <span className="text-muted fw-normal fs-6">
              ({t(`ShareEncumbranceOption_${encumbranceLabel}`)})
            </span>
          </>
        }
        description={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <ul className="list-inline">
            <li>
              {t('Range')}:&nbsp;
              <span className="text-base fw-bold">
                {`${formatNumber(data.from)} - ${formatNumber(data.to)}`}
              </span>
            </li>
          </ul>
        }
        actions={data.isAdmin && !data.isReadOnly ? pageActions : []}
      />

      <EncumbranceDetailsPageBody />

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default EncumbranceDetails;
